<template>
  <v-dialog v-model="dialogVisible" max-width="880px" persistent overlay-opacity="0.75"
    content-class="tutorial-carousel-dialog-card rounded-md sm:rounded-lg overflow-hidden shadow-md">
    <v-card
      class="tutorial-card bg-white rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-100 flex flex-col">
      <!-- Header -->
      <v-card-title
        class="d-flex justify-space-between align-center px-4 sm:px-6 py-3.5 sm:py-4 border-b border-gray-100 bg-white">
        <div class="d-flex align-center min-w-0 mr-2">
          <div
            class="bg-blue-50 border border-blue-100 p-2 sm:p-2.5 rounded-xl mr-3 flex items-center justify-center shrink-0">
            <v-icon color="primary" small>mdi-play-circle</v-icon>
          </div>
          <div class="min-w-0">
            <div class="d-flex align-center flex-wrap gap-1.5">
              <span class="text-sm sm:text-base font-bold tracking-tight text-gray-900 truncate">
                {{ activeTutorial?.title || 'Platform Tutorial' }}
              </span>
              <span v-if="tutorialList.length > 1"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-primary border border-blue-100">
                Step {{ currentIndex + 1 }} of {{ tutorialList.length }}
              </span>
            </div>
            <p class="text-xs text-gray-500 mb-0 font-normal truncate max-w-xl">
              {{ activeTutorial?.description || 'Learn how to navigate and make the most of your platform' }}
            </p>
          </div>
        </div>

        <v-btn icon small class="text-gray-400 hover:text-gray-700 hover:bg-gray-100 shrink-0" @click="closeTutorial"
          aria-label="Close Tutorial">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Carousel Viewport with Floating Arrows -->
      <v-card-text class="pa-0 bg-black relative tutorial-carousel-wrapper">
        <v-carousel v-if="tutorialList && tutorialList.length > 0" v-model="currentIndex" :continuous="false"
          :cycle="false" :show-arrows="false" :hide-delimiters="true" :height="computedCarouselHeight"
          class="tutorial-v-carousel w-full">
          <v-carousel-item v-for="(tutorial, idx) in tutorialList" :key="tutorial.id || idx" class="h-full w-full">
            <div class="relative w-full h-full bg-black flex items-center justify-center select-none overflow-hidden">
              <!-- Active Slide: Render interactive YouTube iframe -->
              <iframe v-if="dialogVisible && currentIndex === idx" class="w-full h-full border-0"
                :src="getEmbedUrl(tutorial.videoUrl)" :title="tutorial.title || 'Platform Tutorial Video'"
                frameborder="0"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen></iframe>

              <!-- Inactive Slide: Render Thumbnail Poster + Play Button overlay (Prevents background audio/heavy DOM) -->
              <div v-else class="relative w-full h-full flex items-center justify-center bg-gray-950 cursor-pointer"
                @click="goToSlide(idx)">
                <img v-if="getThumbnailUrl(tutorial.videoUrl)" :src="getThumbnailUrl(tutorial.videoUrl)"
                  :alt="tutorial.title" class="w-full h-full object-cover opacity-60" />
                <div class="absolute inset-0 bg-black/40 flex flex-col items-center justify-center p-4">
                  <div
                    class="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-2xl transition-transform hover:scale-110">
                    <v-icon size="30" color="white">mdi-play</v-icon>
                  </div>
                  <span
                    class="text-white text-xs sm:text-sm font-semibold mt-3 text-center drop-shadow-md truncate max-w-sm">
                    {{ tutorial.title }}
                  </span>
                </div>
              </div>
            </div>
          </v-carousel-item>
        </v-carousel>

        <!-- Empty State Fallback -->
        <div v-else class="w-full flex flex-col items-center justify-center text-white text-center py-16"
          :style="{ height: computedCarouselHeight + 'px' }">
          <v-icon size="48" color="grey lighten-1">mdi-video-off-outline</v-icon>
          <div class="mt-3 text-sm text-gray-300 font-medium">No tutorial videos available at this time</div>
        </div>

        <!-- Floating Carousel Navigation Arrows (Left / Right) -->
        <template v-if="tutorialList.length > 1">
          <!-- Previous Arrow -->
          <transition name="fade">
            <button v-if="currentIndex > 0" type="button" class="carousel-nav-arrow carousel-arrow-prev"
              @click.stop="prevTutorial" aria-label="Previous Tutorial Video">
              <v-icon size="22" color="white">mdi-chevron-left</v-icon>
            </button>
          </transition>

          <!-- Next Arrow -->
          <transition name="fade">
            <button v-if="currentIndex < tutorialList.length - 1" type="button"
              class="carousel-nav-arrow carousel-arrow-next" @click.stop="nextTutorial"
              aria-label="Next Tutorial Video">
              <v-icon size="22" color="white">mdi-chevron-right</v-icon>
            </button>
          </transition>
        </template>
      </v-card-text>

      <!-- Action Footer with Centered Indicator Dots & Action Button -->
      <v-card-actions
        class="px-4 sm:px-6 py-3.5 sm:py-4 bg-gray-50 relative flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-100">
        <!-- Left: Don't show again checkbox -->
        <div class="w-full sm:w-auto flex items-center justify-start">
          <v-checkbox v-model="dontShowAgain" label="Don't show this tutorial on login again" dense hide-details
            class="mt-0 text-xs sm:text-sm font-medium text-gray-700"></v-checkbox>
        </div>

        <!-- Center: Carousel Indicator Dots (Bottom Center) -->
        <div v-if="tutorialList.length > 1"
          class="carousel-dots-wrapper sm:absolute sm:left-1/2 sm:-translate-x-1/2 flex items-center justify-center gap-2 py-1 z-10">
          <button v-for="(t, idx) in tutorialList" :key="'dot-' + (t.id || idx)" type="button"
            class="carousel-dot-indicator focus:outline-none cursor-pointer"
            :class="{ 'dot-active': currentIndex === idx }" @click="goToSlide(idx)"
            :aria-label="`Jump to slide ${idx + 1}: ${t.title || ''}`" :title="t.title || `Slide ${idx + 1}`"></button>
        </div>

        <!-- Right: Done / Got it Button -->
        <div class="w-full sm:w-auto flex items-center justify-end">
          <v-btn color="primary" depressed small
            class="normal-case font-weight-bold px-6 rounded-lg text-white shadow-sm w-full sm:w-auto"
            @click="closeTutorial">
            {{ currentIndex >= tutorialList.length - 1 ? 'Got it' : 'Done' }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'TutorialCarouselDialog',
  props: {
    platform: {
      type: String,
      default: 'admin'
    },
    useCase: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dialogVisible: false,
      tutorialList: [],
      currentIndex: 0,
      dontShowAgain: false,
      loadingTutorials: false
    }
  },
  computed: {
    activeTutorial() {
      if (this.tutorialList && this.tutorialList.length > 0) {
        return this.tutorialList[this.currentIndex] || this.tutorialList[0]
      }
      return null
    },
    canSkipCurrentTutorial() {
      return this.activeTutorial?.isSkippable !== false
    },
    computedCarouselHeight() {
      if (this.$vuetify.breakpoint.xsOnly) return 220
      if (this.$vuetify.breakpoint.smAndDown) return 300
      return 450
    }
  },
  watch: {
    dialogVisible(val) {
      if (val) {
        window.addEventListener('keydown', this.handleKeydown)
      } else {
        window.removeEventListener('keydown', this.handleKeydown)
      }
    }
  },
  async mounted() {
    this.$root.$on('open-tutorial', this.open)
    await this.checkAndShowOnLogin()
  },
  beforeDestroy() {
    this.$root.$off('open-tutorial', this.open)
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    async open(options = {}) {
      if (typeof options.index === 'number') {
        this.currentIndex = options.index
      } else {
        this.currentIndex = 0
      }

      if (!this.tutorialList || this.tutorialList.length === 0) {
        await this.fetchTutorials()
      }

      this.dialogVisible = true
    },
    async fetchTutorials() {
      try {
        this.loadingTutorials = true
        const currentUseCase = this.useCase || this.$store?.state?.useCase || 'default'
        const isMobile = this.$vuetify.breakpoint.smAndDown
        const device = isMobile ? 'mobile' : 'desktop'

        const res = await this.$axios.get(
          `/tutorials?useCase=${currentUseCase}&platform=${this.platform}&device=${device}`
        )

        const tutorials = res.data?.data?.tutorials || []
        this.tutorialList = tutorials.filter((t) => !!t?.videoUrl)
      } catch (error) {
        console.error('Error fetching tutorials:', error)
        this.tutorialList = []
      } finally {
        this.loadingTutorials = false
      }
    },
    async checkAndShowOnLogin() {
      const dismissed = localStorage.getItem('tutorial_video_dismissed') === 'true'
      if (dismissed) return

      const justLoggedIn = sessionStorage.getItem('just_logged_in') === 'true'
      const sessionChecked = sessionStorage.getItem('tutorial_session_checked') === 'true'

      // Trigger if user freshly logged in or on initial session visit if not yet checked
      if (justLoggedIn || !sessionChecked) {
        sessionStorage.setItem('tutorial_session_checked', 'true')
        sessionStorage.removeItem('just_logged_in')

        await this.fetchTutorials()

        if (this.tutorialList && this.tutorialList.length > 0) {
          // Allow layout components to mount smoothly before opening dialog
          setTimeout(() => {
            this.currentIndex = 0
            this.dialogVisible = true
          }, 800)
        }
      }
    },
    goToSlide(index) {
      if (index >= 0 && index < this.tutorialList.length) {
        this.currentIndex = index
      }
    },
    nextTutorial() {
      if (this.currentIndex < this.tutorialList.length - 1) {
        this.currentIndex++
      }
    },
    prevTutorial() {
      if (this.currentIndex > 0) {
        this.currentIndex--
      }
    },
    skipTutorial() {
      if (this.currentIndex < this.tutorialList.length - 1) {
        this.currentIndex++
      } else {
        this.closeTutorial()
      }
    },
    closeTutorial() {
      if (this.dontShowAgain) {
        localStorage.setItem('tutorial_video_dismissed', 'true')
      }
      this.dialogVisible = false
    },
    handleKeydown(e) {
      if (!this.dialogVisible) return
      if (e.key === 'ArrowRight') {
        this.nextTutorial()
      } else if (e.key === 'ArrowLeft') {
        this.prevTutorial()
      } else if (e.key === 'Escape') {
        this.closeTutorial()
      }
    },
    getYoutubeId(url) {
      if (!url) return ''
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
      const match = String(url).match(regExp)
      return match && match[2].length === 11 ? match[2] : ''
    },
    getEmbedUrl(url) {
      if (!url) return ''
      let embedUrl = url
      const videoId = this.getYoutubeId(url)
      if (videoId) {
        embedUrl = `https://www.youtube.com/embed/${videoId}`
      }
      const separator = embedUrl.includes('?') ? '&' : '?'
      return `${embedUrl}${separator}rel=0&enablejsapi=1`
    },
    getThumbnailUrl(url) {
      const videoId = this.getYoutubeId(url)
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
      }
      return ''
    }
  }
}
</script>

<style scoped>
.tutorial-card {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.tutorial-carousel-wrapper {
  overflow: hidden;
}

/* Floating Navigation Arrows */
.carousel-nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 25;
  width: 42px;
  height: 42px;
  border-radius: 9999px;
  background: rgba(11, 35, 83, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.carousel-nav-arrow:hover {
  background: #0B2353;
  transform: translateY(-50%) scale(1.1);
  border-color: rgba(255, 255, 255, 0.6);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
}

.carousel-nav-arrow:active {
  transform: translateY(-50%) scale(0.95);
}

.carousel-arrow-prev {
  left: 14px;
}

.carousel-arrow-next {
  right: 14px;
}

/* Carousel Indicator Dots */
.carousel-dot-indicator {
  height: 7px;
  width: 7px;
  border-radius: 9999px;
  background-color: #cbd5e1;
  border: none;
  padding: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.carousel-dot-indicator:hover {
  background-color: #94a3b8;
  transform: scale(1.2);
}

.carousel-dot-indicator.dot-active {
  width: 28px;
  background: linear-gradient(90deg, #0B2353, #2563EB);
  box-shadow: 0 1px 4px rgba(11, 35, 83, 0.3);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
